import React, { useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Fade from "@mui/material/Fade"

import { useT } from "i18n/translations"
import { alpha, branding, palette } from "theme/colors"
import { fontDisplay } from "theme/fonts"

const BOOKING_URL_DEFAULT =
  "https://www.booking.com/hotel/pt/casa-da-avo-miquinhas.pt-pt.html"

const HEADER_HEIGHT = 72

const Header = (props) => {
  const {
    scrolled,
    lightHeader,
    lang,
    navItems = [],
    siteSettings = {},
    allLanguages = [],
    announcementBar,
  } = props
  const tr = useT(lang)
  const announcementRef = useRef(null)

  const bookingUrl =
    siteSettings.bookingUrl ||
    process.env.GATSBY_BOOKING_URL ||
    BOOKING_URL_DEFAULT

  const [menuOpen, setMenuOpen] = useState(false)
  const [announcementHeight, setAnnouncementHeight] = useState(0)

  const mainNavItems = navItems.filter((i) => i.type !== "cta")
  const ctaItems = navItems.filter((i) => i.type === "cta")
  const currentLang = allLanguages.find(
    (l) => l.code === lang?.toUpperCase(),
  ) || { code: "PT", flag: "🇵🇹", urlPrefix: "" }

  const headerTop = announcementBar ? announcementHeight : 0

  useEffect(() => {
    if (!menuOpen || typeof document === "undefined") return undefined

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false)
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [menuOpen])

  useEffect(() => {
    if (!announcementBar || !announcementRef.current) {
      setAnnouncementHeight(0)
      return undefined
    }

    const measure = () => {
      setAnnouncementHeight(announcementRef.current?.offsetHeight || 0)
    }

    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [announcementBar])

  // Bar transparency — all pages, independent of lightHeader
  const headerBg = menuOpen
    ? "transparent"
    : scrolled
      ? "rgba(255,255,255,0.96)"
      : "transparent"

  // Element colors — white at top only when lightHeader (Hero A + no page_settings.background)
  const textColor = menuOpen
    ? palette.dark
    : lightHeader && !scrolled
      ? palette.white
      : palette.dark

  const overlayLinkSx = (accent = false) => ({
    color: accent ? palette.accent : palette.dark,
    textDecoration: "none",
    fontSize: {
      xs: "clamp(2rem, 6vw, 2.75rem)",
      md: "clamp(2.5rem, 4vw, 3.5rem)",
      lg: "clamp(2.75rem, 3.5vw, 4rem)",
    },
    fontFamily: fontDisplay,
    fontWeight: 400,
    lineHeight: 1.1,
    letterSpacing: "-0.5px",
    opacity: accent ? 1 : 0.85,
    py: { xs: 0.75, md: 1 },
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    transition: "opacity 0.2s, color 0.2s",
    "&:hover": {
      opacity: 1,
      color: accent ? palette.accentDark : palette.primary,
    },
  })

  const soonBadgeSx = {
    fontSize: { xs: "0.625rem", md: "0.6875rem" },
    fontFamily: "inherit",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    px: 1.25,
    py: 0.35,
    borderRadius: "100px",
    bgcolor: "rgba(196, 120, 74, 0.12)",
    color: palette.accent,
    flexShrink: 0,
  }

  const renderMainNavItem = (item, i) => {
    if (item.type === "soon") {
      return (
        <Box
          key={i}
          aria-disabled="true"
          sx={{
            ...overlayLinkSx(),
            gap: { xs: 1, md: 1.5 },
            flexWrap: "wrap",
            cursor: "default",
            pointerEvents: "none",
            opacity: 0.45,
            "&:hover": {
              opacity: 0.45,
              color: palette.dark,
            },
          }}
        >
          {item.link.title}
          <Box component="span" sx={soonBadgeSx}>
            {tr.nav.soon}
          </Box>
        </Box>
      )
    }

    return (
      <Box
        key={i}
        component={Link}
        to={item.link.url}
        onClick={() => setMenuOpen(false)}
        sx={overlayLinkSx()}
      >
        {item.link.title}
      </Box>
    )
  }

  const languageSwitcher = (variant = "header") => {
    if (allLanguages.length <= 1) return null

    const inOverlay = variant === "overlay"

    return (
      <Box
        component="nav"
        aria-label="Language"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: inOverlay ? { xs: 1.5, md: 2 } : 0.5,
          flexShrink: 0,
        }}
      >
        {allLanguages.map((l, index) => (
          <React.Fragment key={l.code}>
            {index > 0 && (
              <Box
                component="span"
                sx={{
                  color: inOverlay ? "rgba(38,38,38,0.25)" : textColor,
                  opacity: inOverlay ? 1 : 0.35,
                  fontSize: "12px",
                  userSelect: "none",
                  mx: inOverlay ? 0 : 0.25,
                }}
              >
                |
              </Box>
            )}
            <Box
              component={Link}
              to={l.urlPrefix || "/"}
              onClick={() => setMenuOpen(false)}
              sx={{
                  color: inOverlay
                    ? l.code === lang?.toUpperCase()
                      ? palette.dark
                      : "rgba(38,38,38,0.45)"
                    : l.code === lang?.toUpperCase()
                      ? textColor
                      : textColor === palette.white
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(38,38,38,0.45)",
                textDecoration: "none",
                fontSize: inOverlay
                  ? { xs: "1rem", md: "0.875rem" }
                  : { xs: "12px", sm: "13px" },
                fontWeight: l.code === lang?.toUpperCase() ? 600 : 500,
                letterSpacing: "0.5px",
                minWidth: 28,
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { color: inOverlay ? palette.dark : textColor },
                transition: "color 0.15s",
              }}
            >
              {l.code}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    )
  }

  return (
    <>
      {announcementBar && (
        <Box
          ref={announcementRef}
          sx={{
            bgcolor: palette.dark,
            color: palette.white,
            textAlign: "center",
            py: "10px",
            px: 2,
            fontSize: "0.8125rem",
            letterSpacing: "0.3px",
            position: "relative",
            zIndex: 1301,
          }}
        >
          {announcementBar}
        </Box>
      )}

      {/* Fixed header bar — always visible, sits above overlay */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: headerTop,
          left: 0,
          right: 0,
          height: HEADER_HEIGHT,
          zIndex: 1300,
          bgcolor: headerBg,
          backdropFilter: !menuOpen && scrolled ? "blur(8px)" : "none",
          transition: "background-color 0.35s ease, top 0.2s ease",
          display: "flex",
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 5, lg: 8 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            to={currentLang.urlPrefix + "/"}
            style={{ display: "inline-flex", alignItems: "center" }}
            onClick={() => setMenuOpen(false)}
          >
            <img
              src="/images/logo.png"
              alt={siteSettings.siteTitle || "Casa da Avó Miquinhas"}
              style={{
                height: 38,
                width: "auto",
                filter:
                  textColor === palette.white ? "brightness(0) invert(1)" : "none",
                transition: "filter 0.3s",
              }}
            />
          </Link>
        </Box>

        <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5, md: 2.5 },
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: menuOpen ? { xs: "none", sm: "flex" } : "flex" }}>
          {languageSwitcher("header")}
        </Box>

          <Button
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: textColor,
              border: `1.5px solid ${textColor}`,
              borderRadius: "100px",
              px: { xs: 2, sm: 2.5, md: 3 },
              py: "7px",
              fontSize: { xs: "13px", sm: "15px" },
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              display: menuOpen ? "none" : { xs: "none", sm: "inline-flex" },
              transition: "color 0.3s, border-color 0.3s, background-color 0.3s",
              "&:hover": {
                bgcolor: textColor,
                color: textColor === palette.white ? palette.dark : palette.white,
              },
            }}
          >
            {tr.header.book} →
          </Button>

          <Box
            component="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? tr.header.closeMenu : tr.header.openMenu}
            aria-expanded={menuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: textColor,
              fontFamily: "inherit",
              transition: "color 0.3s",
              p: 1,
              ml: -0.5,
              lineHeight: 1,
              minWidth: 44,
              minHeight: 44,
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 14,
                position: "relative",
                flexShrink: 0,
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    height: "2px",
                    bgcolor: "currentColor",
                    borderRadius: "1px",
                    top:
                      i === 0
                        ? 0
                        : i === 1
                          ? "calc(50% - 0.75px)"
                          : "calc(100% - 1.5px)",
                    transformOrigin: "center",
                    transition: "transform 0.25s ease, opacity 0.2s",
                    ...(menuOpen &&
                      i === 0 && {
                        transform: "translateY(6.5px) rotate(45deg)",
                      }),
                    ...(menuOpen && i === 1 && { opacity: 0 }),
                    ...(menuOpen &&
                      i === 2 && {
                        transform: "translateY(-6.5px) rotate(-45deg)",
                      }),
                  }}
                />
              ))}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                letterSpacing: "0.5px",
                display: { xs: "none", sm: "inline" },
              }}
            >
              {menuOpen ? tr.header.close : tr.header.menu}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Full-page overlay menu — frosted glass, all breakpoints */}
      <Fade in={menuOpen} timeout={400}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={tr.header.openMenu}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1299,
            display: menuOpen ? "flex" : "none",
            flexDirection: "column",
            height: "100dvh",
            maxHeight: "100dvh",
            pt: `${headerTop + HEADER_HEIGHT}px`,
            pb: "max(20px, env(safe-area-inset-bottom))",
            overflow: "hidden",
            bgcolor: alpha.menuOverlay,
            backdropFilter: "blur(24px) saturate(110%)",
            WebkitBackdropFilter: "blur(24px) saturate(110%)",
          }}
        >
          <Box
            component="nav"
            aria-label="Main"
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 0.5, md: 0.75 },
              width: "100%",
              maxWidth: 720,
              mx: "auto",
              px: { xs: 3, sm: 4, md: 6 },
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {mainNavItems.map(renderMainNavItem)}
            {ctaItems.map((item, i) => (
              <Box
                key={`cta-${i}`}
                component={Link}
                to={item.link.url}
                onClick={() => setMenuOpen(false)}
                sx={overlayLinkSx(true)}
              >
                {item.link.title}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              maxWidth: 1400,
              mx: "auto",
              px: { xs: 3, sm: 4, md: 6 },
              pt: 2,
              pb: { xs: 1, sm: 0 },
            }}
          >
            {languageSwitcher("overlay")}

            <Button
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              sx={{
                bgcolor: palette.accent,
                color: palette.white,
                border: "none",
                borderRadius: "100px",
                px: { xs: 3.5, md: 5 },
                py: 1.75,
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                fontWeight: 600,
                textTransform: "none",
                minHeight: 48,
                ml: "auto",
                flexShrink: 0,
                boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: palette.accentDark },
              }}
            >
              {tr.header.bookNow} →
            </Button>
          </Box>
        </Box>
      </Fade>
    </>
  )
}

export default Header
